import React, { useEffect, useMemo, useRef, useState } from 'react';
import { components, GroupBase, MenuListProps } from 'react-select';
import { Virtuoso, VirtuosoHandle } from 'react-virtuoso';

/** Only sizes the menu box until Virtuoso reports a measured height. */
const ESTIMATED_ROW_HEIGHT = 37;

type Row = { element: React.ReactElement, data?: unknown };

/**
 * The internal shape react-select gives a group: its own Heading component plus the props to
 * render it with. This is the thing most likely to move under a react-select upgrade.
 */
type GroupChildProps = {
  Heading?: React.ComponentType<Record<string, unknown>>,
  headingProps?: Record<string, unknown>,
  label?: React.ReactNode,
  options?: unknown[],
  children?: React.ReactNode,
  data?: unknown,
};

/**
 * react-select hands a group over as one element carrying its options as children, so
 * virtualising the children it gives us would window the 45 CPV divisions and still mount
 * all 1 688 codes. Flattening to [heading, option, option, ...] is what makes the count
 * that matters the one being windowed.
 *
 * The heading is rebuilt from the group's own Heading component so it keeps react-select's
 * styling rather than a copy of it.
 */
export const flattenRows = (children: React.ReactNode): Row[] => {
  const rows: Row[] = [];

  React.Children.forEach(children, (child) => {
    if (!React.isValidElement(child)) return;
    const props = child.props as GroupChildProps;

    if (props?.Heading && Array.isArray(props?.options)) {
      const { Heading, headingProps, label, children: groupChildren, ...commonProps } = props;
      rows.push({
        element: (
          <Heading {...commonProps} {...headingProps} key={`${child.key}-heading`}>
            {label}
          </Heading>
        ),
      });
      React.Children.forEach(groupChildren, (option) => {
        if (React.isValidElement(option)) {
          rows.push({ element: option, data: (option.props as GroupChildProps)?.data });
        }
      });
      return;
    }

    rows.push({ element: child, data: props?.data });
  });

  return rows;
};

/**
 * A menu that renders only the options in view and fills the rest in as you scroll.
 *
 * react-select builds its whole menu in one pass, and every option here carries an
 * Emotion-serialised style object and a checkbox on top of its own nodes. At the 1 688 CPV
 * codes and 1 112 buyers a Sweden-wide TED sync produces, opening the dropdown stalls for
 * seconds.
 */
export const VirtualizedMenuList = <Option, IsMulti extends boolean, Group extends GroupBase<Option>>(
  props: MenuListProps<Option, IsMulti, Group>,
) => {
  const { children, maxHeight, focusedOption, theme } = props;
  const rows = useMemo(() => flattenRows(children), [children]);
  const virtuoso = useRef<VirtuosoHandle | null>(null);
  const [measuredHeight, setMeasuredHeight] = useState(0);

  // Arrow-key focus moves through options that are no longer mounted, so react-select's own
  // scroll-into-view has nothing to scroll to; the list has to follow the focus itself.
  const focusedIndex = rows.findIndex((row) => row.data !== undefined && row.data === focusedOption);

  useEffect(() => {
    if (focusedIndex >= 0) virtuoso.current?.scrollIntoView({ index: focusedIndex });
  }, [focusedIndex]);

  // The empty-state and loading notices are a single node with nothing to window.
  if (!rows.length) return <components.MenuList {...props}>{children}</components.MenuList>;

  /**
   * react-select's menu list is a scroll container of its own - maxHeight with overflowY
   * auto - and pads itself by baseUnit top and bottom. Filling it to the full maxHeight
   * therefore left it scrollable by exactly that padding: a second scroller, and a wheel
   * gesture latches onto one element for its whole duration, so it spent those few pixels
   * and then stopped. Dragging the scrollbar never had the problem, since that targets
   * Virtuoso's scroller directly.
   *
   * Leaving the padding out of the list's height is all it takes: the two add up to
   * maxHeight, so the outer box has nothing to scroll. Its own styles are left alone -
   * pinning it shut instead cost the list its scrollbar altogether.
   */
  const listHeight = Math.min(
    maxHeight - theme.spacing.baseUnit * 2,
    measuredHeight || rows.length * ESTIMATED_ROW_HEIGHT,
  );

  return (
    <components.MenuList {...props}>
      <Virtuoso
        ref={virtuoso}
        // Shrinks to the content while it is short, and stops at the menu's own limit.
        // overscroll-behavior replaces what ScrollCaptor was for: a gesture that
        // reaches the end of the list must not scroll the page behind the menu.
        style={{ height: listHeight, overscrollBehavior: 'contain' }}
        totalCount={rows.length}
        totalListHeightChanged={setMeasuredHeight}
        computeItemKey={(index) => rows[index].element.key ?? index}
        itemContent={(index) => rows[index].element}
      />
    </components.MenuList>
  );
};

export default VirtualizedMenuList;
